import React, { useEffect, useState, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Backdrop, CircularProgress, Link, Box, Modal, Fade, Typography, TextField, Button, Autocomplete } from '@mui/material';
import './CreateOrganization.css';

// APIs & Authorization
import { getOrganisation, getOrgUsers, updateOrganisation } from '../../../../apis/organisations.api';
import { fetchUsers, getUserRoles } from '../../../../apis/users.api';
import { AuthContext } from '../../../../AuthContext';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// Custom Popper component
const CustomPopper = styled(Popper)(({ theme }) => ({
  width: 'auto !important',
  minWidth: '45%',
  zIndex: theme.zIndex.modal,
  
}));

function EditOrganization({ 
  openEditOrganization, 
  setOpenEditOrganization, 
  selectedOrg, 
  setIsToastOpen, 
  setToastMessage, 
  setLoading,
  setErrorCode 
}) {
  const { authData } = useContext(AuthContext);
  const { access_token, token_type } = authData;
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState({
    name: '',
    users: [{ ourId: null, name: null, role: null }]
  });

  useEffect(() => {
    const getOrgData= async () => {
      if (selectedOrg && access_token && token_type) {
        try {
          const orgData = await getOrganisation({ queryKey: ['organisation', { selectedOrg, access_token, token_type }] });
          setFormValues((prevValues) => ({
            ...prevValues,
            name: orgData?.name || '',
          }));
        } catch (error) {
          console.error("Error fetching organization data:", error);
        }
      }
    }
    const getOrgUserData = async () => {
      if (selectedOrg && access_token && token_type) {
        try {
          const orgUsersData = await getOrgUsers({ queryKey: ['orgUsers', {
            selectedOrg, access_token, token_type }] });
          setFormValues((prevValues) => ({
            ...prevValues,
            users: orgUsersData.length > 0 ? orgUsersData.map((user) =>
              ({ ourId: user.our_id, name: user.name, role: user.role_name })) : [{ ourId: null, name: null, role: null }]
          }));
        } catch (error) {       
          console. error("Error fetching organization users:", error);
        }
      }
    }
    getOrgData();
    getOrgUserData();
  }, []);

  const { data: getUsers, isLoading: loadingUsers } = useQuery({
      queryKey : ['users', { access_token, token_type }],
      queryFn : fetchUsers,
      enabled: !!access_token && !!token_type
    }
  );

  const { data: getUserOrgRoles, isLoading: loadingUserOrgRoles } = useQuery({
    queryKey : ['userOrgRoles', {role_type:"org_role", access_token, token_type}],
    queryFn : getUserRoles,
      enabled: !!access_token
    }
  );

  const handleAddNewUser = () => {
    setFormValues((prevValues) => ({
      ...prevValues,
      users: [...prevValues.users, { ourId: null, name: null, role: null }]
    }));
  };

  const handleUserChange = (index, field, value) => {
    const newUsers = [...formValues.users];
    newUsers[index][field] = value;
    setFormValues((prevValues) => ({
      ...prevValues,
      users: newUsers
    }));
  };

  const handleRemoveUser = (index) => {
    const newUsers = formValues.users.filter((_, i) => i !== index);
    setFormValues((prevValues) => ({
      ...prevValues,
      users: newUsers.length > 0 ? newUsers : [{ ourId: null, name: null, role: null }]
    }));
  };

  const findUserId = (userName) => {
    const user = getUsers?.find((u) => u.name === userName);
    return user ? user.user_id : 0;
  };

  const findRoleId = (roleName) => {
    const role = getUserOrgRoles?.find((r) => r.role_name === roleName);
    return role ? role.role_id : 0;
  };

  const mutation = useMutation({
    mutationFn : updateOrganisation,
    onSuccess: res => {
      queryClient.invalidateQueries('organisations');
      setToastMessage('Organisation has been updated successfully');
      setErrorCode(res?.status);
      setLoading(false);
      setIsToastOpen(true);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setToastMessage(`Error: ${errorMessage}`);
      setErrorCode(error?.response?.status);
      setLoading(false);
      setIsToastOpen(true);
    }
  });

  const EditOrganizationFormSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: formValues.name,
      org_user_roles: formValues.users.map((user) => ({
        our_id: user.ourId,
        user_id: findUserId(user.name),
        role_id: findRoleId(user.role)
      }))
    };

    setLoading(true);
    setOpenEditOrganization(false);
    mutation.mutate({ selectedOrg, payload, access_token, token_type });
  };

  return (
    <>
    {
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openEditOrganization}
        onClose={() => setOpenEditOrganization(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500
          }
        }}
        className="createOrganizationModal"
      >
        <Fade in={openEditOrganization}>
          <Box className="createOrganizationModalClass">
            <div className="createOrganizationModalHeader">
              <Typography
                id="transition-modal-title"
                variant="h6"
                component="h2"
                className="createOrganizationModalTitle"
              >
                Edit Organization
              </Typography>
              <span
                className="createOrganizationModalClose"
                onClick={() => setOpenEditOrganization(false)}
              >
                &times;
              </span>
            </div>
            <form className="g-3" onSubmit={EditOrganizationFormSubmit}>
              <div className="createOrganizationModalBody">
                <div className="d-flex" style={{ margin: '16px 0', position: 'relative' }}>
                  <div className="col-6" style={{ paddingRight: '12px' }}>
                    <label htmlFor="org-name" className="form-label">
                      Organization Name<span style={{color: 'red'}}>&#65290;</span>
                    </label>
                    <TextField
                      required
                      disabled
                      id="org-name"
                      placeholder="organization name"
                      sx={{ width: "100%" }}
                      onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                      value={formValues?.name}
                    />
                  </div>
                </div>
                <div className='superAdminTitle'>Users</div>
                <div style={{ margin: '16px 0' }}>
                  {formValues?.users?.map((user, index) => (
                    <div key={index} style={{ marginTop: '16px' }}>
                      <Link
                        sx={{
                          display: 'flex',
                          textDecoration: 'none',
                          justifyContent: 'flex-end',
                          marginRight: '12px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleRemoveUser(index)}
                      >
                        Remove
                      </Link>
                      <div className="d-flex">
                        <div className="col-6" style={{ paddingRight: '12px' }}>
                          <label htmlFor="username" className="form-label">Username</label>
                          <Autocomplete                         
                            disablePortal
                            options={getUsers || []}
                            getOptionLabel={(option) => option?.name}
                            value={getUsers?.find((u) => u.name === user.name) || null}
                            onChange={(event, newValue) => {
                              handleUserChange(index, 'name', newValue ? (newValue?.name) : null);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                placeholder="Enter Username"
                                name="name"
                                onChange={(e) => handleUserChange(index, 'name', e.target.value)}
                                 sx={{ width: "100%" }}                              
                              />
                            )}
                            PopperComponent={(props) => <CustomPopper {...props} placement='bottom-start'/>}
                          />
                        </div>
                        <div className="col-6" style={{ paddingRight: '12px' }}>
                          <label htmlFor="role" className="form-label">Role</label>
                          <Autocomplete
                            disablePortal
                            options={getUserOrgRoles || []}
                            getOptionLabel={(option) => option?.role_name}
                            value={getUserOrgRoles?.find((r) => r.role_name === user.role) || null}
                            onChange={(event, newValue) => {
                              handleUserChange(index, 'role', newValue ? (newValue?.role_name) : null);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                placeholder="Enter role"
                                name="role"
                                onChange={(e) => handleUserChange(index, 'role', e.target.value)}
                                sx={{ width: "100%" }}
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleAddNewUser}
                    sx={{
                      textTransform: 'capitalize',
                      fontFamily: 'open sans',
                      fontWeight: 400,
                      borderRadius: '0',
                      m: '16px 0'
                    }}
                  >
                    Add more
                  </Button>
                </div>
              </div>
              <div className="createOrganizationFormButtons">
                <Button variant="outlined" size="large" onClick={() => setOpenEditOrganization(false)}>
                  Cancel
                </Button>
                <Button variant="contained" size="large" type="submit">Submit</Button>
              </div>
            </form>
          </Box>
        </Fade>
      </Modal>
    }
    </>
  );
}

export default EditOrganization;
